import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  IconX,
  IconUser,
  IconClock,
  IconLock,
  IconRotateClockwise,
  IconDots,
  IconPaperclip,
  IconFile,
  IconPhoto,
  IconDownload,
  IconSend,
  IconEdit,
  IconTrash,
} from '@tabler/icons-react';
import styles from './FeedbackDetail.module.scss';
import {
  useGetFeedbackId,
  useGetFeedbackIdComments,
  usePostFeedbackIdComments,
  usePutFeedbackId,
  usePutFeedbackIdClose,
  usePutFeedbackIdReopen,
  getGetFeedbackQueryKey,
  getGetFeedbackIdQueryKey,
  getGetFeedbackIdCommentsQueryKey,
} from '../../api/feedback-api/feedback-api';
import { FeedbackStatus, FeedbackPriority } from '../../api/model';
import type { FeedbackTicketDto, FeedbackAttachmentDto, FeedbackType } from '../../api/model';
import {
  TYPE_ICON,
  STATUS_COLOR,
  PRIORITY_COLOR,
  TYPE_VALUES,
  PRIORITY_VALUES,
  canClose,
  canReopen,
} from './feedbackMeta';
import { formatRelative, formatDateTime, ticketRef } from './feedbackFormat';
import {
  ACCEPT_ATTR,
  MAX_FILES,
  MAX_FILE_BYTES,
  isAllowedType,
  formatFileSize,
  fileToPayload,
  downloadAttachment,
} from './feedbackAttachments';
import ConfirmDialog from './ConfirmDialog';

interface FeedbackDetailProps {
  ticketId: number | null;
  fallbackTicket: FeedbackTicketDto | null;
  isOpen: boolean;
  onClose: () => void;
}

const tint = (hex: string) => `${hex}1a`;

const FeedbackDetail: React.FC<FeedbackDetailProps> = ({ ticketId, fallbackTicket, isOpen, onClose }) => {
  const { t, i18n } = useTranslation('feedback');
  const locale = i18n.language;
  const queryClient = useQueryClient();

  const enabled = isOpen && ticketId != null;
  const { data: fullTicket } = useGetFeedbackId(ticketId ?? 0, { query: { enabled } });
  const { data: commentsData } = useGetFeedbackIdComments(ticketId ?? 0, { query: { enabled } });

  const ticket = fullTicket ?? fallbackTicket;
  const comments = commentsData ?? [];

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    type: undefined as FeedbackType | undefined,
    priority: undefined as FeedbackPriority | undefined,
  });
  const [composer, setComposer] = useState('');
  const [composerFiles, setComposerFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'close' | 'reopen' | 'editSave' | 'send' | null>(null);
  const [sending, setSending] = useState(false);
  const composerInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditing(false);
    setComposer('');
    setComposerFiles([]);
    setFileError(null);
    setActionError(null);
    setConfirm(null);
  }, [ticketId]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: getGetFeedbackQueryKey() });
    if (ticketId != null) {
      queryClient.invalidateQueries({ queryKey: getGetFeedbackIdQueryKey(ticketId) });
      queryClient.invalidateQueries({ queryKey: getGetFeedbackIdCommentsQueryKey(ticketId) });
    }
  };

  const commentMutation = usePostFeedbackIdComments({
    mutation: {
      onSuccess: () => {
        invalidateAll();
        setComposer('');
        setComposerFiles([]);
      },
    },
  });
  const editMutation = usePutFeedbackId({
    mutation: {
      onSuccess: () => {
        invalidateAll();
        setEditing(false);
      },
      onError: () => setActionError(t('errors.editFailed')),
    },
  });
  const closeMutation = usePutFeedbackIdClose({
    mutation: {
      onSuccess: () => {
        invalidateAll();
        setConfirm(null);
      },
      onError: () => {
        setConfirm(null);
        setActionError(t('errors.closeFailed'));
      },
    },
  });
  const reopenMutation = usePutFeedbackIdReopen({
    mutation: {
      onSuccess: () => {
        invalidateAll();
        setConfirm(null);
      },
      onError: () => {
        setConfirm(null);
        setActionError(t('errors.reopenFailed'));
      },
    },
  });

  if (!ticket) {
    return (
      <div className={`${styles.overlay} ${isOpen ? styles.open : ''}`} onClick={onClose}>
        <aside className={`${styles.panel} ${isOpen ? styles.open : ''}`} onClick={(e) => e.stopPropagation()} />
      </div>
    );
  }

  const TypeIcon = ticket.type ? TYPE_ICON[ticket.type] : IconDots;
  const statusColor = ticket.status ? STATUS_COLOR[ticket.status] : '#6b7280';
  const priorityColor = ticket.priority ? PRIORITY_COLOR[ticket.priority] : '#6b7280';
  const isClosed = ticket.status === FeedbackStatus.CLOSED;
  const editable = ticket.status === FeedbackStatus.OPEN && !comments.some((c) => c.authorIsAdmin);
  const mutating = closeMutation.isPending || reopenMutation.isPending;
  const lastComment = comments[comments.length - 1];
  const awaitingReply = !!lastComment && !lastComment.authorIsAdmin;

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    setFileError(null);
    const next = [...composerFiles];
    for (const file of Array.from(list)) {
      if (next.length >= MAX_FILES) {
        setFileError(t('attachments.tooMany', { count: MAX_FILES }));
        break;
      }
      if (file.size > MAX_FILE_BYTES) {
        setFileError(t('attachments.tooLarge', { name: file.name }));
        continue;
      }
      if (!isAllowedType(file.type)) {
        setFileError(t('attachments.unsupported', { name: file.name }));
        continue;
      }
      next.push(file);
    }
    setComposerFiles(next);
    if (composerInputRef.current) composerInputRef.current.value = '';
  };

  const handleDownload = async (att: FeedbackAttachmentDto) => {
    if (att.id == null) return;
    try {
      await downloadAttachment(att.id, att.fileName || 'attachment');
    } catch {
      setActionError(t('attachments.downloadFailed'));
    }
  };

  const requestSend = () => {
    if (!composer.trim() || sending) return;
    setActionError(null);
    setConfirm('send');
  };

  const sendMessage = async () => {
    if (!composer.trim() || sending || ticketId == null) return;
    setActionError(null);
    setSending(true);
    try {
      const attachments = composerFiles.length ? await Promise.all(composerFiles.map(fileToPayload)) : undefined;
      await commentMutation.mutateAsync({ id: ticketId, data: { body: composer.trim(), attachments } });
    } catch {
      setActionError(t('errors.commentFailed'));
    } finally {
      setSending(false);
    }
  };

  const startEdit = () => {
    setEditForm({
      title: ticket.title ?? '',
      description: ticket.description ?? '',
      type: ticket.type,
      priority: ticket.priority,
    });
    setActionError(null);
    setEditing(true);
  };

  const submitEdit = () => {
    if (ticketId == null) return;
    editMutation.mutate({
      id: ticketId,
      data: {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        type: editForm.type,
        priority: editForm.priority,
      },
    });
  };

  const handleConfirm = (value?: string) => {
    if (ticketId == null) return;
    if (confirm === 'close') closeMutation.mutate({ id: ticketId });
    else if (confirm === 'reopen') reopenMutation.mutate({ id: ticketId, data: { comment: value } });
    else if (confirm === 'editSave') {
      setConfirm(null);
      submitEdit();
    } else if (confirm === 'send') {
      setConfirm(null);
      void sendMessage();
    }
  };

  const renderAttachments = (atts?: FeedbackAttachmentDto[]) =>
    atts && atts.length > 0 ? (
      <div className={styles.attachmentList}>
        {atts.map((att) => (
          <button key={att.id} type="button" className={styles.attachmentChip} onClick={() => handleDownload(att)}>
            {att.contentType?.startsWith('image/') ? <IconPhoto size={14} /> : <IconFile size={14} />}
            <span className={styles.attName}>{att.fileName}</span>
            <span className={styles.attSize}>{formatFileSize(att.sizeBytes ?? 0)}</span>
            <IconDownload size={14} className={styles.attDownload} />
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div className={`${styles.overlay} ${isOpen ? styles.open : ''}`} onClick={onClose}>
      <aside
        className={`${styles.panel} ${isOpen ? styles.open : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className={styles.header}>
          <div className={styles.headTop}>
            <span className={styles.ref}>{ticketRef(ticket.id)}</span>
            {ticket.status && (
              <span className={styles.statusBadge} style={{ color: statusColor, backgroundColor: tint(statusColor) }}>
                {t(`statuses.${ticket.status}`)}
              </span>
            )}
            <span className={styles.spacer} />
            {editable && !editing && (
              <button className={styles.editButton} type="button" onClick={startEdit}>
                <IconEdit size={15} />
                {t('actions.edit')}
              </button>
            )}
            <button className={styles.closeButton} onClick={onClose} type="button" aria-label={t('detail.back')}>
              <IconX size={18} />
            </button>
          </div>
          {!editing && <h2 className={styles.title}>{ticket.title}</h2>}
        </header>

        <div className={styles.scroll}>
          {editing ? (
            <div className={styles.editForm}>
              <div className={styles.field}>
                <label>{t('create.fields.title')}</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                />
              </div>
              <div className={styles.field}>
                <label>{t('create.fields.type')}</label>
                <div className={styles.typeGrid}>
                  {TYPE_VALUES.map((type) => {
                    const Icon = TYPE_ICON[type];
                    return (
                      <button
                        key={type}
                        type="button"
                        className={`${styles.typeOption} ${editForm.type === type ? styles.typeActive : ''}`}
                        onClick={() => setEditForm((f) => ({ ...f, type }))}
                      >
                        <Icon size={16} />
                        <span>{t(`types.${type}`)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className={styles.field}>
                <label>{t('create.fields.priority')}</label>
                <select
                  value={editForm.priority ?? ''}
                  onChange={(e) => setEditForm((f) => ({ ...f, priority: e.target.value as FeedbackPriority }))}
                >
                  {PRIORITY_VALUES.map((p) => (
                    <option key={p} value={p}>
                      {t(`priorities.${p}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label>{t('create.fields.description')}</label>
                <textarea
                  rows={6}
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              {actionError && <p className={styles.error}>{actionError}</p>}
              <div className={styles.editActions}>
                <button className={styles.secondaryAction} type="button" onClick={() => setEditing(false)}>
                  {t('edit.buttons.cancel')}
                </button>
                <button
                  className={styles.primaryAction}
                  type="button"
                  disabled={editMutation.isPending || !editForm.title.trim() || !editForm.description.trim()}
                  onClick={() => setConfirm('editSave')}
                >
                  {editMutation.isPending ? t('edit.buttons.saving') : t('edit.buttons.save')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.metaStrip}>
                <span className={styles.metaItem}>
                  <TypeIcon size={16} />
                  {ticket.type ? t(`types.${ticket.type}`) : '—'}
                </span>
                {ticket.priority && (
                  <span
                    className={styles.priorityPill}
                    style={{ color: priorityColor, backgroundColor: tint(priorityColor) }}
                  >
                    {t(`priorities.${ticket.priority}`)}
                  </span>
                )}
                {ticket.reporterName && (
                  <span className={styles.metaItem}>
                    <IconUser size={16} />
                    {ticket.reporterName}
                  </span>
                )}
                <span className={styles.metaItem} title={formatDateTime(ticket.created, locale)}>
                  <IconClock size={16} />
                  {t('detail.created', { date: formatRelative(ticket.created, locale) })}
                </span>
              </div>

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>{t('detail.description')}</h3>
                <p className={styles.description}>{ticket.description}</p>
                {renderAttachments(ticket.attachments)}
              </section>

              {ticket.resolutionOutcome && (
                <div className={styles.resolutionRow}>
                  <span className={styles.resolutionLabel}>{t('detail.resolution')}</span>
                  <span className={styles.resolutionValue}>{t(`resolutions.${ticket.resolutionOutcome}`)}</span>
                </div>
              )}

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>{t('detail.thread')}</h3>
                {comments.length === 0 ? (
                  <p className={styles.noMessages}>{t('detail.noMessages')}</p>
                ) : (
                  <div className={styles.thread}>
                    {comments.map((c) => (
                      <div key={c.id} className={`${styles.message} ${c.authorIsAdmin ? styles.messageAdmin : ''}`}>
                        <div className={styles.messageHead}>
                          <span className={styles.messageAuthor}>{c.authorName || '—'}</span>
                          {c.authorIsAdmin && <span className={styles.roleBadge}>{t('detail.teamBadge')}</span>}
                          <span className={styles.messageTime}>{formatRelative(c.created, locale)}</span>
                        </div>
                        {c.body && <div className={styles.messageBody}>{c.body}</div>}
                        {renderAttachments(c.attachments)}
                      </div>
                    ))}
                  </div>
                )}

                {isClosed ? (
                  <p className={styles.closedNotice}>{t('detail.closedNotice')}</p>
                ) : awaitingReply ? (
                  <p className={styles.closedNotice}>{t('detail.awaitingReply')}</p>
                ) : (
                  <div className={styles.composer}>
                    <textarea
                      value={composer}
                      onChange={(e) => setComposer(e.target.value)}
                      placeholder={t('detail.composerPlaceholder')}
                      rows={3}
                    />
                    {composerFiles.length > 0 && (
                      <ul className={styles.composerFiles}>
                        {composerFiles.map((file, index) => (
                          <li key={`${file.name}-${index}`} className={styles.composerFile}>
                            {file.type.startsWith('image/') ? <IconPhoto size={14} /> : <IconFile size={14} />}
                            <span className={styles.attName}>{file.name}</span>
                            <span className={styles.attSize}>{formatFileSize(file.size)}</span>
                            <button
                              type="button"
                              className={styles.fileRemove}
                              onClick={() => setComposerFiles((prev) => prev.filter((_, i) => i !== index))}
                            >
                              <IconTrash size={13} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {fileError && <p className={styles.error}>{fileError}</p>}
                    <div className={styles.composerBar}>
                      <input
                        ref={composerInputRef}
                        type="file"
                        multiple
                        accept={ACCEPT_ATTR}
                        onChange={(e) => addFiles(e.target.files)}
                        hidden
                      />
                      <button
                        type="button"
                        className={styles.composerAttach}
                        onClick={() => composerInputRef.current?.click()}
                      >
                        <IconPaperclip size={16} />
                        {t('detail.attach')}
                      </button>
                      <button
                        type="button"
                        className={styles.composerSend}
                        onClick={requestSend}
                        disabled={!composer.trim() || sending}
                      >
                        <IconSend size={15} />
                        {sending ? t('detail.sending') : t('detail.send')}
                      </button>
                    </div>
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        {!editing && (canClose(ticket.status) || canReopen(ticket.status)) && (
          <div className={styles.footer}>
            {actionError && <span className={styles.actionError}>{actionError}</span>}
            <div className={styles.footerButtons}>
              {canReopen(ticket.status) && (
                <button
                  className={styles.secondaryAction}
                  type="button"
                  onClick={() => setConfirm('reopen')}
                  disabled={mutating}
                >
                  <IconRotateClockwise size={16} />
                  {reopenMutation.isPending ? t('actions.reopening') : t('actions.reopen')}
                </button>
              )}
              {canClose(ticket.status) && (
                <button
                  className={styles.primaryAction}
                  type="button"
                  onClick={() => setConfirm('close')}
                  disabled={mutating}
                >
                  <IconLock size={16} />
                  {closeMutation.isPending ? t('actions.closing') : t('actions.close')}
                </button>
              )}
            </div>
          </div>
        )}
      </aside>

      <ConfirmDialog
        open={confirm === 'close'}
        title={t('confirm.close.title')}
        message={t('confirm.close.body')}
        confirmLabel={t('confirm.close.confirm')}
        loading={closeMutation.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
      <ConfirmDialog
        open={confirm === 'reopen'}
        title={t('confirm.reopen.title')}
        message={t('confirm.reopen.body')}
        confirmLabel={t('confirm.reopen.confirm')}
        loading={reopenMutation.isPending}
        prompt={{ placeholder: t('confirm.reopen.placeholder') }}
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
      <ConfirmDialog
        open={confirm === 'editSave'}
        title={t('confirm.editSave.title')}
        message={t('confirm.editSave.body')}
        confirmLabel={t('confirm.editSave.confirm')}
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
      <ConfirmDialog
        open={confirm === 'send'}
        title={t('confirm.send.title')}
        message={t('confirm.send.body')}
        confirmLabel={t('confirm.send.confirm')}
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default FeedbackDetail;
